// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { FunctionOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { ICONS } from '@Constants/iconsApp';
import { Space } from 'antd';
import { FunctionForm, FunctionValuesForm } from './FunctionForm';
import { FunctionEventList } from '../eventList';

interface FunctionManageProps {
    onFinish: (func: FunctionValuesForm) => Promise<any>;
    triggerElem?: ReactElement;
    defaultValues?: FunctionOutput;
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
    eventList: FunctionEventList;
}

export const FunctionManage = ({
    onFinish,
    triggerElem,
    defaultValues,
    defaultVisible = false,
    onVisibleChange,
    visible,
    eventList,
}: FunctionManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!defaultValues,
    });

    const handleOnFinish = (onSuccess: () => void) => async (functionValuesFormData: FunctionValuesForm) => {
        const { another, ...data } = functionValuesFormData;

        try {
            await onFinish(data);
            setFieldsTouched(false);
            onFormValuesChange();

            if (another || !defaultValues) {
                form.resetFields();
                onSuccess();
            }
        } catch (error) {}
    };

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.FunctionIcon />
                    {!!defaultValues ? i('function.manage.drawer.edit') : i('function.manage.drawer.create')}
                </Space>
            }
            triggerElem={triggerElem}
            defaultVisible={defaultVisible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            visible={visible}
            disableClose={fieldsTouched}
            width="70vw"
        >
            {(toggleDrawer) => (
                <FunctionForm
                    form={form}
                    isValid={isValid && fieldsTouched}
                    defaultValues={defaultValues}
                    onFinish={handleOnFinish(() => toggleDrawer())}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        setFieldsTouched(false);
                        form.resetFields();
                    }}
                    eventList={eventList}
                />
            )}
        </THDrawer>
    );
};
